import React from "react";
import { Button, Typography, Card, Row, Col, Space, Divider, Tag } from "antd";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  MailOutlined,
  ClockCircleOutlined,
  UserAddOutlined,
  LoginOutlined,
  CheckCircleOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import joinBg from "../../../../images/join.svg";
import logoSvg from "../../../forms/LoginForm/logo_alt.svg";
import KeycloakService from "../../../contexts/auth/keycloakService";

const { Title, Text, Paragraph } = Typography;

const OnboardingContactPage = () => {
  const query = new URLSearchParams(useLocation().search);
  const redirectPayload = useSelector(state => state.auth?.redirectPayload);
  const email = query.get("email") || redirectPayload?.email || "";

  return (
    <Row
      style={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        background: "#f5f7fa",
      }}
      align="middle"
      justify="center"
    >
      {/* Left - Illustration with branding */}
      <Col xs={0} sm={0} md={0} lg={11} xl={11}>
        <div
          style={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(160deg, #0a8080 0%, #0d9e9e 40%, #11b3b3 100%)",
            padding: "40px 32px",
          }}
        >
          <img
            src={joinBg}
            alt="Welcome"
            style={{
              width: "70%",
              maxWidth: 300,
              marginBottom: 32,
              filter: "brightness(1.1)",
            }}
          />
          <Title
            level={3}
            style={{ color: "#fff", marginBottom: 8, textAlign: "center" }}
          >
            Join the FoodLabz Platform
          </Title>
          <Paragraph
            style={{
              color: "rgba(255,255,255,0.85)",
              textAlign: "center",
              maxWidth: 320,
              fontSize: 14,
            }}
          >
            Trusted by leading food testing laboratories and quality assurance
            teams across the industry.
          </Paragraph>
          <Space size={8} style={{ marginTop: 12 }}>
            <Tag
              color="rgba(255,255,255,0.2)"
              style={{
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
              }}
            >
              <CheckCircleOutlined /> Secure
            </Tag>
            <Tag
              color="rgba(255,255,255,0.2)"
              style={{
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
              }}
            >
              <CheckCircleOutlined /> Compliant
            </Tag>
            <Tag
              color="rgba(255,255,255,0.2)"
              style={{
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
              }}
            >
              <CheckCircleOutlined /> Fast
            </Tag>
          </Space>
        </div>
      </Col>

      {/* Right - Card content */}
      <Col xs={23} sm={20} md={16} lg={13} xl={13}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "0 16px",
          }}
        >
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              boxShadow: "0 8px 40px rgba(0,0,0,0.06)",
              width: "100%",
              maxWidth: 460,
            }}
            bodyStyle={{ padding: "28px 32px 24px" }}
          >
            {/* Logo */}
            <div style={{ textAlign: "center", marginBottom: 12 }}>
              <img src={logoSvg} alt="FoodLabz" style={{ height: 34 }} />
            </div>

            <Divider style={{ margin: "12px 0 18px" }} />

            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #e6f7f7, #ccefef)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                }}
              >
                <UserAddOutlined style={{ fontSize: 24, color: "#0a8080" }} />
              </div>
              <Title level={4} style={{ marginBottom: 6, color: "#1a1a2e" }}>
                Almost There!
              </Title>
              <Paragraph style={{ color: "#666", fontSize: 13, margin: 0 }}>
                {email ? (
                  <>
                    <Text strong style={{ color: "#0a8080" }}>
                      {email}
                    </Text>{" "}
                    is not yet registered.
                  </>
                ) : (
                  "Your account is not yet registered on FoodLabz."
                )}{" "}
                Contact our team to complete your onboarding.
              </Paragraph>
            </div>

            {/* Steps / What happens next */}
            <div
              style={{
                background: "#fafbfc",
                borderRadius: 10,
                padding: "14px 18px",
                marginBottom: 16,
              }}
            >
              <Text
                strong
                style={{
                  fontSize: 13,
                  color: "#333",
                  display: "block",
                  marginBottom: 10,
                }}
              >
                <RocketOutlined style={{ color: "#0a8080", marginRight: 6 }} />
                How to get started
              </Text>
              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "#0a8080",
                      color: "#fff",
                      fontSize: 11,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontWeight: 600,
                    }}
                  >
                    1
                  </div>
                  <Text style={{ fontSize: 12, color: "#555" }}>
                    Reach out to our operations team via email
                  </Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "#0a8080",
                      color: "#fff",
                      fontSize: 11,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontWeight: 600,
                    }}
                  >
                    2
                  </div>
                  <Text style={{ fontSize: 12, color: "#555" }}>
                    We'll set up your company account & permissions
                  </Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "#0a8080",
                      color: "#fff",
                      fontSize: 11,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontWeight: 600,
                    }}
                  >
                    3
                  </div>
                  <Text style={{ fontSize: 12, color: "#555" }}>
                    Log in and start using FoodLabz!
                  </Text>
                </div>
              </Space>
            </div>

            {/* Contact Card */}
            <div
              style={{
                border: "1px solid #e8f4f4",
                borderRadius: 10,
                padding: "14px 18px",
                marginBottom: 18,
                background: "#fff",
              }}
            >
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <Space align="center" size={12}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      background: "#e6f7f7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MailOutlined style={{ fontSize: 16, color: "#0a8080" }} />
                  </div>
                  <div>
                    <Text
                      style={{
                        fontSize: 11,
                        color: "#999",
                        display: "block",
                        lineHeight: 1,
                      }}
                    >
                      Email Us
                    </Text>
                    <a
                      href="mailto:support@agnext.in"
                      style={{
                        color: "#0a8080",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      support@agnext.in
                    </a>
                  </div>
                </Space>
                <Space align="center" size={12}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      background: "#e6f7f7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ClockCircleOutlined
                      style={{ fontSize: 16, color: "#0a8080" }}
                    />
                  </div>
                  <div>
                    <Text
                      style={{
                        fontSize: 11,
                        color: "#999",
                        display: "block",
                        lineHeight: 1,
                      }}
                    >
                      Support Hours
                    </Text>
                    <Text style={{ fontWeight: 500, fontSize: 13 }}>
                      Mon – Fri, 9:00 AM – 6:00 PM IST
                    </Text>
                  </div>
                </Space>
              </Space>
            </div>

            {/* Back Button */}
            <Button
              type="primary"
              icon={<LoginOutlined />}
              block
              size="large"
              onClick={() => {
                if (
                  process.env.REACT_APP_ENABLE_KEYCLOAK === "true" ||
                  process.env.REACT_APP_ENABLE_KEYCLOAK_DIRECT_LOGIN === "true"
                ) {
                  localStorage.removeItem("kc_token");
                  sessionStorage.clear();
                  KeycloakService.logout();
                } else {
                  window.location.href = "/";
                }
              }}
              style={{
                background: "linear-gradient(135deg, #0a8080, #0d9e9e)",
                borderColor: "#0a8080",
                borderRadius: 8,
                height: 42,
                fontWeight: 500,
                boxShadow: "0 2px 8px rgba(10,128,128,0.3)",
              }}
            >
              Sign in with a different account
            </Button>
          </Card>
        </div>
      </Col>
    </Row>
  );
};

export default OnboardingContactPage;
